import { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  FiChevronRight as FiChevronRightIcon,
  FiChevronsLeft as FiChevronsLeftIcon,
  FiChevronsRight as FiChevronsRightIcon,
} from "react-icons/fi";

// Custom Components
import Anchors from "../UI/Anchors";
import NotificationPopup from "../UI/notification/NotificationPopup";
// Hooks
import { useAccessControl } from "../../hooks";

import "./style.css";
import { images } from "../../assets/images";
import Image from "../UI/Image";
import { useTheme } from "../../context/theme/ThemeContext";

const SIDEBAR_COLLAPSED_KEY = "sidebar:collapsed";

// PROPOSED — not wired into any .env file yet, see comment at its use below.
// REACT_APP_ENV is not currently set anywhere in this repo (staging and prod
// .env files on the EC2 boxes only differ on things like
// REACT_APP_BASE_URL_v1). Until someone adds REACT_APP_ENV=production to the
// prod .env, this reads as undefined everywhere and IS_PRODUCTION is false,
// so behaviour is unchanged in both environments.
const IS_PRODUCTION = process.env.REACT_APP_ENV === "production";

const ChevronIcon = FiChevronRightIcon as React.FC<any>;
const ChevronsLeftIcon = FiChevronsLeftIcon as React.FC<any>;
const ChevronsRightIcon = FiChevronsRightIcon as React.FC<any>;

// DimeRec / DimeXplain / DimeAI keep their full-colour brand logos; every
// other item uses the cyan icon set, recoloured per theme via CSS mask (see
// style.css).
const BRAND_ICONS = new Set(["R", "X", "AI"]);

// DimeAI's own sidebar entry keeps the full-colour brand brain (icon: "AI",
// via BRAND_ICONS above); these five siblings get their own simple
// maskable icon, forced amber regardless of section, so they read as
// distinct from DimeAI itself and from each other (2026-10).
const AMBER_ICONS = new Set([
  "DIMEAI_USAGE_AMBER",
  "DIMEAI_GUARDRAILS_AMBER",
  "DIMEAI_FEEDBACK_AMBER",
  "DIMEAI_REFERENCE_DATA_AMBER",
  "DIMEAI_TAG_INDEPENDENT_AMBER",
]);

const SidebarIcon = ({
  icon,
  name,
  emerald,
  indigo,
}: {
  icon: keyof typeof images;
  name: string;
  emerald?: boolean;
  // Trust Center icons only -- see .sidebar-icon-indigo in style.css.
  indigo?: boolean;
}) => {
  if (BRAND_ICONS.has(icon as string)) {
    return (
      <Image className="sidebar-brand-icon" src={images[icon]} alt={name} />
    );
  }
  return (
    <span
      className={`sidebar-icon ${
        emerald
          ? "sidebar-icon-emerald"
          : indigo
            ? "sidebar-icon-indigo"
            : AMBER_ICONS.has(icon as string)
              ? "sidebar-icon-amber"
              : ""
      }`}
      role="img"
      aria-label={name}
      style={{
        WebkitMaskImage: `url(${images[icon]})`,
        maskImage: `url(${images[icon]})`,
      }}
    />
  );
};

type SidebarItem = {
  id: number;
  name: string;
  link: string;
  hasAccess: boolean;
  service?: string;
  icon: keyof typeof images;
  isGlobal?: boolean;
  // DimeAI's own link ("/dime-ai/") is a *prefix* of its sibling tabs'
  // links ("/dime-ai/usage/", "/dime-ai/guardrails/", etc.), so the normal
  // startsWith active-check below left the DimeAI item highlighted on every
  // one of those sub-pages too -- it only cleared once you clicked a nav
  // item that didn't start with "/dime-ai/". exactMatch opts an item out of
  // that prefix match without touching isGlobal, which also changes how the
  // link itself is built (see getFullLink) and would break DimeAI's URL.
  exactMatch?: boolean;
};

type SidebarSection = {
  type: "section";
  section: string;
  items: SidebarItem[];
  // Icon used for the collapsed-sidebar flyout trigger. Falls back to the
  // first visible item's icon when omitted — only needed for sections whose
  // items are brand logos (e.g. My Apps), where that fallback would be
  // misleading.
  icon?: keyof typeof images;
};

type SidebarSingle = {
  type: "single";
  item: SidebarItem;
};

type SidebarConfig = (SidebarSection | SidebarSingle)[];

const Sidebar = () => {
  const { currentService } = useParams();
  const location = useLocation();

  const {
    checkPermission,
    checkSpecificModulePermission,
    IS_GLOBAL_ADMIN,
    IS_CHECKER,
    IS_MAKER,
    IS_REGIONAL_ADMIN,
    IS_SYSTEM_ADMIN,
    IS_COA_MANAGER,
    IS_COA_VIEWER,
  } = useAccessControl();

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true",
  );
  const [openFlyouts, setOpenFlyouts] = useState<Record<string, boolean>>({});
  const sidebarRef = useRef<HTMLUListElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleFlyout = (section: string) => {
    setOpenFlyouts((prev) => (prev[section] ? {} : { [section]: true }));
  };

  useEffect(() => {
    if (!Object.values(openFlyouts).some(Boolean)) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (!sidebarRef.current?.contains(e.target as Node)) {
        setOpenFlyouts({});
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openFlyouts]);

  // A Global Admin who is also a Maker sees a genuinely identical dashboard
  // under both roles (Main.tsx/DashboardOverview.tsx don't branch on
  // maker vs. global_admin at all — only System Admin gets different
  // content). Two sidebar links pointing at pixel-identical pages reads as
  // a bug, so collapse them into one combined entry instead. Scoped tightly
  // to exactly this combination (not Regional Admin or System Admin) so
  // every other role combo keeps its existing separate-link behavior.
  const isDualMakerGlobalAdmin =
    IS_MAKER && IS_GLOBAL_ADMIN && !IS_REGIONAL_ADMIN && !IS_SYSTEM_ADMIN;

  // Same idea, for Checker + Global Admin: Checker Dashboard (Main.tsx,
  // role === "checker") already renders everything Admin Dashboard does
  // for a Global Admin, plus the "Member (Maker)" filter and the
  // Approved/Not Approved/Rejected ratio donut -- a strict superset, not a
  // different view. Unlike the Maker combo above, this one keeps the plain
  // "Checker Dashboard" label rather than a combined name (per explicit
  // instruction) -- only the redundant "Admin Dashboard" link is suppressed.
  const isDualCheckerGlobalAdmin =
    IS_CHECKER && IS_GLOBAL_ADMIN && !IS_REGIONAL_ADMIN && !IS_SYSTEM_ADMIN;

  const dashboardTitle = () => {
    if (isDualMakerGlobalAdmin) return "Global Admin / Maker";
    if (IS_MAKER) return "Maker";
    if (IS_CHECKER) return "Checker";
    if (IS_GLOBAL_ADMIN) return "Global Admin";
    if (IS_REGIONAL_ADMIN) return "Regional Admin";
    if (IS_SYSTEM_ADMIN) return "System Admin";
    return "Dashboard";
  };

  const sidebarConfig: SidebarConfig = [
    {
      type: "section",
      section: dashboardTitle(),
      items: [
        {
          id: 4,
          name: isDualMakerGlobalAdmin
            ? "Global Admin / Maker Dashboard"
            : "Maker Dashboard",
          link: "/maker/",
          hasAccess: isDualMakerGlobalAdmin || IS_MAKER,
          icon: "DASHBOARD_CYAN",
        },
        {
          id: 5,
          name: "Checker Dashboard",
          link: "/checker/",
          hasAccess: IS_CHECKER,
          icon: "DASHBOARD_CYAN",
        },
        {
          id: 6,
          name: "Admin Dashboard",
          link: "/admin/",
          // Suppressed for either dual combo — the merged Maker item, or
          // the plain Checker Dashboard item above, already covers it.
          // Every other combination (lone Global Admin, Regional Admin,
          // System Admin) is unaffected.
          hasAccess:
            isDualMakerGlobalAdmin || isDualCheckerGlobalAdmin
              ? false
              : IS_GLOBAL_ADMIN || IS_REGIONAL_ADMIN || IS_SYSTEM_ADMIN,
          icon: "DASHBOARD_CYAN",
        },
      ],
    },
    {
      type: "section",
      section: "My Apps",
      icon: "APPS_CYAN",
      items: [
        {
          id: 2,
          name: "DimeRec",
          link: "/records/",
          hasAccess: checkPermission("LIST_OF_REC"),
          icon: "R",
        },
        {
          id: 7,
          name: "DimeXplain",
          link: "/dime-xplain/",
          hasAccess: true,
          service: "dime-explain",
          icon: "X",
        },
        {
          id: 9,
          name: "DimeAI",
          link: "/dime-ai/",
          hasAccess: true,
          icon: "AI",
          // Its own link is a prefix of every sibling tab below
          // ("/dime-ai/usage/", "/dime-ai/guardrails/", "/dime-ai/feedback/",
          // "/dime-ai/reference-data/"), so without this it stayed
          // highlighted on all of them via the default startsWith match.
          exactMatch: true,
        },
        {
          id: 11,
          name: "DimeAI Usage",
          link: "/dime-ai/usage/",
          // DimeAI Usage/Guardrails restricted to System Admin in production
          // only — staging intentionally shows this to all roles for testing
          // purposes. This is by design, not a bug (see 2026-09-26). Staging
          // accounts other than System Admin rely on seeing these links to
          // exercise the feature before a System Admin login is available
          // there; once REACT_APP_ENV=production is set on the prod .env
          // (not yet done — see IS_PRODUCTION above), only System Admin will
          // see this link in production. Do not "fix" this divergence
          // between main and develop without checking with product first.
          hasAccess: IS_PRODUCTION
            ? IS_SYSTEM_ADMIN
            : IS_SYSTEM_ADMIN || IS_GLOBAL_ADMIN || IS_REGIONAL_ADMIN,
          icon: "DIMEAI_USAGE_AMBER",
        },
        {
          id: 10,
          name: "DimeAI Guardrails",
          link: "/dime-ai/guardrails/",
          // DimeAI Usage/Guardrails restricted to System Admin in production
          // only — staging intentionally shows this to all roles for testing
          // purposes. This is by design, not a bug (see 2026-09-26). See the
          // matching comment on "DimeAI Usage" above and on the route in
          // routers/index.ts.
          hasAccess: IS_PRODUCTION
            ? IS_SYSTEM_ADMIN
            : IS_SYSTEM_ADMIN || IS_GLOBAL_ADMIN,
          icon: "DIMEAI_GUARDRAILS_AMBER",
        },
        {
          id: 12,
          name: "DimeAI Feedback",
          link: "/dime-ai/feedback/",
          // System Admin only in every environment — this shows real user
          // questions and answers, not aggregate stats or config, so it does
          // not get the staging-only wider-audience exception the two
          // entries above have.
          hasAccess: IS_SYSTEM_ADMIN,
          icon: "DIMEAI_FEEDBACK_AMBER",
        },
        {
          id: 13,
          name: "DimeAI Reference Data",
          link: "/dime-ai/reference-data/",
          // System Admin only in every environment — same reasoning as
          // Feedback above: these tables steer what DimeAI treats as
          // equivalent/distinct terminology for every user.
          hasAccess: IS_SYSTEM_ADMIN,
          icon: "DIMEAI_REFERENCE_DATA_AMBER",
        },
        {
          id: 25,
          name: "DimeAI Test Bench",
          link: "/dime-ai/test-bench/",
          // System Admin only in every environment: it asks real questions
          // of real organisations' books.
          hasAccess: IS_SYSTEM_ADMIN,
          icon: "DIMEAI_FEEDBACK_AMBER",
        },
        {
          id: 24,
          name: "DimeAI Tag-Independent Mode",
          link: "/dime-ai/tag-independent-mode/",
          // System Admin only, in every environment — this decides which
          // companies/periods DimeAI answers without Tag filtering applied,
          // a data-exposure control same tier as Reference Data/Feedback.
          hasAccess: IS_SYSTEM_ADMIN,
          icon: "DIMEAI_TAG_INDEPENDENT_AMBER",
        },
      ],
    },
    {
      type: "section",
      section: "Chart of Accounts (CoA)",
      items: [
        {
          id: 20,
          name: "Configure CoA",
          link: "/coa/",
          hasAccess:
            IS_COA_MANAGER ||
            IS_COA_VIEWER ||
            IS_GLOBAL_ADMIN ||
            IS_REGIONAL_ADMIN ||
            IS_SYSTEM_ADMIN,
          icon: "CONFIGURE_COA",
        },
      ],
    },
    {
      type: "section",
      section: "Admin Settings",
      items: [
        {
          id: 1,
          name: "Setup & Invite",
          link: "/invite/",
          hasAccess: checkPermission("INVITE_REGIONAL_ADMIN"),
          icon: "SETUP_INVITE_CYAN",
        },
        {
          id: 3,
          name: "Company Config",
          link: "/companies/",
          hasAccess: checkPermission("ACCESS_COMPANIES"),
          icon: "COMPANY_CONFIG_CYAN",
        },
        {
          id: 16,
          name: "Upload Data",
          link: "/upload-gl/",
          hasAccess: Boolean(checkSpecificModulePermission("upload_gl")),
          icon: "DATA_UPLOAD",
        },
        {
          id: 21,
          name: "Machine Learning",
          link: "/ml-mapping/",
          hasAccess: IS_SYSTEM_ADMIN,
          icon: "AI_TRANSPARENCY_CYAN",
        },
        {
          id: 22,
          name: "Audit Trail",
          link: "/audit-trail/",
          // Unlike Machine Learning (System Admin only), Global/Regional
          // Admin also see this -- scoped server-side to companies they
          // have access to (see AuditLogAppService.list_entries).
          hasAccess: IS_SYSTEM_ADMIN || IS_GLOBAL_ADMIN || IS_REGIONAL_ADMIN,
          icon: "MANAGE_DATA_CYAN",
        },
        {
          id: 23,
          name: "Privacy Requests",
          link: "/privacy-requests/",
          // System-Admin-only fulfillment queue for DPDPA/CCPA/GDPR
          // data-subject requests -- unlike Audit Trail, Global/Regional
          // Admin don't get a view onto this.
          hasAccess: IS_SYSTEM_ADMIN,
          icon: "MANAGE_DATA_CYAN",
        },
      ],
    },

    {
      type: "section",
      section: "Trust Center",
      icon: "TRUST_CENTER_CYAN",
      items: [
        {
          id: 11,
          name: "Read our Privacy Policy",
          link: "/privacy-policy/",
          hasAccess: true,
          icon: "PRIVACY_POLICY_CYAN",
          // NOT isGlobal here (unlike the pre-login route in routers/index.ts)
          // -- this needs the normal /:currentService prefix so it keeps
          // reading as an ordinary Trust Center item. See the routers/index.ts
          // comment on the second /privacy-policy/ route entry for why:
          // making this isGlobal broke every other sidebar link (all read as
          // /undefined/...) while this page was open.
        },
        {
          id: 12,
          name: "Contact Privacy Team",
          link: "/contact-privacy-team/",
          // A System Admin IS the privacy team fulfilling these requests --
          // this customer-facing "contact us" entry doesn't apply to them.
          hasAccess: !IS_SYSTEM_ADMIN,
          icon: "CONTACT_PRIVACY_CYAN",
        },
        {
          id: 13,
          name: "Manage Your Data",
          link: "/manage-your-data/",
          // Same reasoning as "Contact Privacy Team" above: for a System
          // Admin this page just shows the same fulfillment queue as the
          // dedicated Admin Settings > Privacy Requests page -- keeping
          // both was a redundant near-duplicate, so this customer
          // self-service entry is hidden for System Admin.
          hasAccess: !IS_SYSTEM_ADMIN,
          icon: "TRUST_MANAGE_DATA_CYAN",
        },
        {
          id: 14,
          name: "AI & Algorithm Transparency",
          link: "/ai-algorithm-transparency/",
          hasAccess: true,
          icon: "TRUST_AI_TRANSPARENCY_CYAN",
        },
        {
          id: 15,
          name: "Sub-Processors",
          link: "/sub-processors/",
          hasAccess: true,
          icon: "SUB_PROCESSORS_CYAN",
        },
      ],
    },
    {
      type: "single",
      item: {
        id: 8,
        name: "Notifications",
        link: "#",
        hasAccess: true,
        icon: "NOTIFICATIONS_CYAN",
      },
    },
    {
      type: "single",
      item: {
        id: 10,
        name: "Support",
        link: "/support/",
        hasAccess: true,
        icon: "SUPPORT_CYAN",
      },
    },
  ];

  const isActive = (item: SidebarItem) => {
    if (item.link === "#") return false;
    if (item.isGlobal) return location.pathname === item.link;
    if (item.exactMatch)
      return location.pathname === `/${currentService}${item.link}`;
    return location.pathname.startsWith(`/${currentService}${item.link}`);
  };

  const getFullLink = (item: SidebarItem) => {
    if (item.link === "#") return "#";
    if (item.isGlobal) return item.link;
    return `/${currentService}${item.link}`;
  };

  return (
    <>
      <ul
        ref={sidebarRef}
        className={`navbar-nav sidebar accordion ${collapsed ? "sidebar-collapsed" : ""}`}
        id="accordionSidebar"
      >
        {sidebarConfig.map((entry) => {
          // 🔥 SINGLE ITEM (no section)
          if (entry.type === "single") {
            const item = entry.item;
            if (!item.hasAccess) return null;
            const isNotificationsItem = item.name === "Notifications";

            return (
              <li
                className={`nav-item d-flex align-items-center gap-2 px-3 ${isActive(item) ? "active" : ""}`}
                key={item.id}
              >
                {isNotificationsItem ? (
                  <NotificationPopup
                    placement="rightTop"
                    triggerClassName="sidebar-notification-trigger"
                    triggerContent={
                      <Anchors
                        className="nav-link d-flex align-items-center gap-2 sidebar-tooltip"
                        to={"#"}
                        data-tooltip={item.name}
                      >
                        <SidebarIcon icon={item.icon} name={item.name} />
                        {!collapsed && (
                          <span
                            className="text-dark notification-label sidebar-label"
                            style={{
                              fontFamily: "Poppins !important",
                              fontSize: "16px",
                            }}
                          >
                            {item.name}
                          </span>
                        )}
                      </Anchors>
                    }
                  />
                ) : (
                  <Anchors
                    className="nav-link d-flex align-items-center gap-2 sidebar-tooltip"
                    to={getFullLink(item)}
                    data-tooltip={item.name}
                  >
                    <SidebarIcon icon={item.icon} name={item.name} />
                    {!collapsed && (
                      <span
                        className="text-dark sidebar-label"
                        style={{
                          fontFamily: "Poppins !important",
                          fontSize: "16px",
                        }}
                      >
                        {item.name}
                      </span>
                    )}
                  </Anchors>
                )}
              </li>
            );
          }

          // 🔥 SECTION
          const visibleItems = entry.items.filter((item) => item.hasAccess);
          if (!visibleItems.length) return null;

          const isSectionActive = visibleItems.some((item) => isActive(item));

          // The Dashboard section must never collapse, even after
          // navigating away to another section like My Apps > DimeRec —
          // same behavior for every role, System Admin included. (This
          // used to explicitly exclude System Admin, which is exactly why
          // its "Admin Dashboard" item disappeared from the sidebar the
          // moment you opened DimeRec/DimeXplain/DimeAI: isSectionActive
          // went false and there was no override keeping it open.)
          const isDashboardSection = entry.section === dashboardTitle();
          // My Apps (DimeRec/DimeXplain/DimeAI) gets the same always-open
          // treatment, for every user — these are core nav, not something
          // that should require re-expanding after clicking into another
          // section.
          const isAlwaysOpenSection = isDashboardSection || entry.section === "My Apps";
          // Trust Center icons get their own fixed indigo (#7186e8),
          // distinct from the emerald Dashboard section and the default
          // theme-driven cyan everything else uses.
          const isTrustCenterSection = entry.section === "Trust Center";

          const isOpen = isAlwaysOpenSection
            ? true
            : collapsed
              ? true
              : (openSections[entry.section] ?? isSectionActive);

          return (
            <li
              className="nav-item"
              key={entry.section}
              style={{ borderBottom: "1px solid var(--border-subtle)" }}
            >
              {/* 🔥 Section Header */}
              <div
                className="nav-link sidebar-section-header d-flex justify-content-between align-items-center mt-2 px-2"
                style={{ cursor: "pointer" }}
                onClick={() => toggleSection(entry.section)}
              >
                <span className="sidebar-heading m-0">{entry.section}</span>

                {/* 👉 Arrow Button */}
                <button
                  className="btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center justify-content-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection(entry.section);
                  }}
                  style={{
                    transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                  }}
                >
                  <ChevronIcon size={16} />
                </button>
              </div>

              {/* 🔽 Collapsed: one icon, click to open a flyout listing the section's items */}
              {collapsed && visibleItems.length > 1 ? (
                <div
                  className={`nav-item sidebar-flyout-trigger sidebar-tooltip ${
                    openFlyouts[entry.section] ? "sidebar-flyout-open" : ""
                  }`}
                  data-tooltip={entry.section}
                  onMouseLeave={() =>
                    setOpenFlyouts((prev) => (prev[entry.section] ? {} : prev))
                  }
                >
                  <div
                    className="nav-link d-flex align-items-center justify-content-center px-3"
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleFlyout(entry.section)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleFlyout(entry.section);
                      }
                    }}
                  >
                    <SidebarIcon
                      icon={entry.icon || visibleItems[0].icon}
                      name={entry.section}
                      emerald={isDashboardSection}
                      indigo={isTrustCenterSection}
                    />
                  </div>
                  <div className="sidebar-flyout">
                    <div className="sidebar-flyout-panel">
                      <span className="sidebar-flyout-title">
                        {entry.section}
                      </span>
                      {visibleItems.map((item) => (
                        <Anchors
                          key={item.id}
                          className={`sidebar-flyout-link ${isActive(item) ? "active" : ""}`}
                          to={getFullLink(item)}
                          onClick={() => toggleFlyout(entry.section)}
                        >
                          {item.name}
                        </Anchors>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`collapse ${isOpen ? "show" : ""}`}>
                  {visibleItems.map((item) => (
                    <li
                      className={`nav-item d-flex align-items-center gap-2 px-3 ${
                        isActive(item) ? "active" : ""
                      }`}
                      key={item.id}
                    >
                      <Anchors
                        className="nav-link d-flex align-items-center gap-2 sidebar-tooltip"
                        to={getFullLink(item)}
                        data-tooltip={item.name}
                      >
                        <SidebarIcon
                          icon={item.icon}
                          name={item.name}
                          emerald={isDashboardSection}
                          indigo={isTrustCenterSection}
                        />
                        {!collapsed && (
                          <span
                            className={`text-dark sidebar-label${
                              isDualMakerGlobalAdmin &&
                              item.name === "Global Admin / Maker Dashboard"
                                ? " sidebar-label--wrap"
                                : ""
                            }`}
                          >
                            {item.name}
                          </span>
                        )}
                      </Anchors>
                    </li>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {!collapsed && (
        <div className="sidebar-branding">
          <span className="sidebar-branding-text">Powered by</span>
          <Image
            src={isDark ? images.MAIN_LOGO_DARK : images.MAIN_LOGO}
            alt="Repodime Systems"
            className="sidebar-branding-logo"
          />
        </div>
      )}
      <button
        type="button"
        className={`sidebar-collapse-btn ${collapsed ? "sidebar-collapse-btn-collapsed" : ""}`}
        onClick={() => setCollapsed((prev) => !prev)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? (
          <ChevronsRightIcon size={14} />
        ) : (
          <ChevronsLeftIcon size={14} />
        )}
      </button>
    </>
  );
};

export default Sidebar;
